import {
  PriceBookBranchScope,
  PricebookBranch,
  PricebookBranches,
  PricebookBranchWithShops,
  PricebookBranchesUpdateRequest,
} from "../../../../types/price-book.type";
import { numberWithCommas } from "../../../../utils";
import { ALL_BRANCHES_LABEL } from "../constants";

/** How many branch codes a sentence names before it says "and N more". */
const MAX_NAMED_BRANCHES = 5;

/** 00-HO is always open, and a branch whose shops are on the book cannot be taken off it. */
export const isBranchLocked = (branch: PricebookBranch): boolean =>
  branch.is_head_office || branch.shop_count > 0;

/**
 * The branches ticked when the dialog opens: the ones the book is open in, plus every locked
 * one — a switch from ทุกสาขา to เลือกสาขา must keep the branches that have shops on it.
 */
export const initialCheckedCompanyIds = (data: PricebookBranches): string[] =>
  data.branches
    .filter((branch) => branch.is_selected || isBranchLocked(branch))
    .map((branch) => branch.company_id);

/** 00-HO is added by the API, so only the other ticked branches are sent; `all` sends none. */
export const buildBranchesRequest = (
  branchScope: PriceBookBranchScope,
  checkedCompanyIds: string[],
  branches: PricebookBranch[],
): PricebookBranchesUpdateRequest => {
  if (branchScope === "all") return { branch_scope: "all" };
  const checked = new Set(checkedCompanyIds);
  return {
    branch_scope: "selected",
    branch_company_ids: branches
      .filter(
        (branch) => !branch.is_head_office && checked.has(branch.company_id),
      )
      .map((branch) => branch.company_id),
  };
};

/** The API's refusal, followed by each branch that still has shops on the book. */
export const buildBranchesWithShopsMessage = (
  message: string,
  branches: PricebookBranchWithShops[],
): string => {
  const named = branches
    .map(
      (branch) =>
        `${branch.branch_code} ${branch.branch_name} (${numberWithCommas(
          branch.shop_count,
        )} ร้าน)`,
    )
    .join(", ");
  return named ? `${message}: ${named}` : message;
};

/** Codes of the branches a `selected` book is open in (00-HO first, as the API orders them). */
export const selectedBranchCodes = (data: PricebookBranches): string[] =>
  data.branches
    .filter((branch) => branch.is_selected)
    .map((branch) => branch.branch_code);

const nameBranches = (codes: string[]): string => {
  const named = codes.slice(0, MAX_NAMED_BRANCHES).join(", ");
  const remaining = codes.length - MAX_NAMED_BRANCHES;
  return remaining > 0 ? `${named} และอีก ${remaining} สาขา` : named;
};

/**
 * How far a price save reaches: every branch, or the branches the book is open in by name,
 * and the shops on it. `branches` is null while the branch list is still loading or failed,
 * and then the sentence falls back to the book's branch count.
 */
export const buildPriceImpactMessage = (
  branches: PricebookBranches | null,
  branchCount: number,
  customerCount: number,
): string => {
  const customers = `ลูกค้า ${numberWithCommas(customerCount)} ราย`;
  if (!branches) {
    return `ราคาที่บันทึกจะมีผลกับ ${numberWithCommas(branchCount)} สาขาและ${customers}`;
  }
  if (branches.branch_scope === "all" && !branches.is_draft) {
    return `ราคาที่บันทึกจะมีผลกับ${ALL_BRANCHES_LABEL}และ${customers}`;
  }
  return `ราคาที่บันทึกจะมีผลกับสาขา ${nameBranches(
    selectedBranchCodes(branches),
  )} และ${customers}`;
};
